'use client';

import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faUsers, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { Card, NewProfileCardHeader, Typography } from 'src/components';
import { Chip } from 'src/components/PulseUI';

interface Member {
  id: string;
  name: string;
  is_primary?: boolean;
}

const ProfileSiteOfCareMembers = () => {
  const { entityDetails } = useProfile();
  const members: Member[] = entityDetails?.members || [];
  const [isOpen, setIsOpen] = useState(false);

  // Primary member first; the rest keep API order.
  const sortedMembers = useMemo(() => {
    const allMembers: Member[] = entityDetails?.members || [];
    const primary = allMembers.filter((member) => member.is_primary);
    const rest = allMembers.filter((member) => !member.is_primary);
    return [...primary, ...rest];
  }, [entityDetails?.members]);

  const title =
    members.length > 0 ? `Members (${members.length})` : 'Members';

  return (
    <Card
      className="p-6 bg-white rounded-lg outline outline-1 -outline-offset-1 outline-neutral-200 flex flex-col justify-start items-start w-full"
      data-testid="soc-members"
      aria-label="Site of Care Members"
    >
      <div
        role="button"
        tabIndex={0}
        className="w-full flex items-center justify-between gap-2 cursor-pointer focus-visible:outline-none"
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
        aria-expanded={isOpen}
        data-testid="soc-members-toggle"
      >
        <NewProfileCardHeader icon={faUsers} title={title} />
        <Icon
          icon={faChevronDown}
          className={`text-xs text-secondary transition-transform duration-300 ${
            isOpen ? 'rotate-0' : '-rotate-90'
          }`}
          aria-hidden="true"
        />
      </div>

      <div
        className={`grid w-full transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
          isOpen
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        }`}
        aria-hidden={!isOpen}
        data-testid="soc-members-content"
      >
        <div className="overflow-hidden">
          <div className="pt-4">
            {members.length > 0 ? (
              <div
                className="flex flex-col gap-1 w-full"
                data-testid="soc-members-list"
              >
                {sortedMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex flex-col"
                    data-testid={`soc-member-${member.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Typography className="text-sm font-medium text-primary">
                        {member.name}
                      </Typography>
                      {member.is_primary && (
                        <Chip
                          size="sm"
                          variant="soft"
                          color="primary"
                          aria-label="Primary member"
                          data-testid={`soc-member-primary-${member.id}`}
                        >
                          Primary
                        </Chip>
                      )}
                    </div>
                    <Typography className="text-xs text-tertiary">
                      {member.id}
                    </Typography>
                  </div>
                ))}
              </div>
            ) : (
              <Typography className="font-medium text-sm text-primary">
                Not available
              </Typography>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ProfileSiteOfCareMembers;
